import { useMutation } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';

type RecordingDataVersionResponse = {
  dataUploadFileId: string;
};

/**
 * Ensures an independently resolvable copy of a meeting's recording data exists for a client or a
 * whole family, returning the data-upload-file id to review. Used when a meeting has more than one
 * record to process so each can be reviewed without one resolving the others.
 */
export const useCreateRecordingDataVersion = (eventId: string) => {
  const clientMutation = useMutation<{ data: RecordingDataVersionResponse }, Error, string>({
    mutationFn: clientId =>
      api.post<RecordingDataVersionResponse>(
        `calendar-events/${eventId}/recording-data-versions/${clientId}`,
      ),
  });

  const familyMutation = useMutation<{ data: RecordingDataVersionResponse }, Error, string>({
    mutationFn: familyId =>
      api.post<RecordingDataVersionResponse>(
        `calendar-events/${eventId}/recording-data-versions/family/${familyId}`,
      ),
  });

  const createClientDataVersion = async (clientId: string): Promise<string> => {
    const result = await clientMutation.mutateAsync(clientId);
    return result.data.dataUploadFileId;
  };

  const createFamilyDataVersion = async (familyId: string): Promise<string> => {
    const result = await familyMutation.mutateAsync(familyId);
    return result.data.dataUploadFileId;
  };

  return {
    createClientDataVersion,
    createFamilyDataVersion,
    error: clientMutation.error ?? familyMutation.error,
  };
};
